// Central helper for Signal public forms.
//
// Rule: Clinic Command Center is the SINGLE source of truth for leads.
// Every public form must call `submitSignalLead()`. It forwards the lead to
// the `clinic-os-lead-intake` edge function (which server-side holds
// CLINIC_OS_LEAD_INTAKE_URL / TOKEN — never exposed to the browser).
//
// Any legacy secondary actions (send-contact email, local bookings/leads
// insert, WhatsApp automation, analytics) MUST run inside `runNonBlocking`
// so a failure there NEVER blocks the primary lead handoff.
//
// Debug map of where each form sends leads (final destination = Clinic OS):
// - Contact.tsx              -> submitSignalLead (source: Signal Website - Contact Form)
// - BookingForm.tsx          -> submitSignalLead (source: Signal Website - Booking Form)
// - QuickCallbackForm.tsx    -> submitSignalLead (source: Signal Website - Quick Callback)
// - CandidacyTest.tsx        -> submitSignalLead (source: Signal Website - Candidacy Test)
// - ExitIntentPopup.tsx      -> submitSignalLead (source: Signal Website - Exit Intent Popup)
// - PDFDownloadModal.tsx     -> submitSignalLead (source: Signal Website - PDF Download)
// - HairAnalysis.tsx         -> submitSignalLead (source: Signal Website - Hair Analysis)
// - BookConsultation.tsx     -> submitSignalLead (source: Signal Website - Book Consultation)
// - GoogleAdsLanding.tsx     -> submitSignalLead (source: Signal Website - Google Ads Landing)
// - PricingPopup.tsx         -> not a form (WhatsApp/CTA links only)

import { clinicOsDuplicateMessage, type ClinicOsLead, type ClinicOsResult } from "@/lib/clinicOsIntake";
import { submitWebsiteLeadForm, type WebsiteFormType } from "@/lib/submitWebsiteLeadForm";
import { trackLeadFormSubmit, trackBookingCompleted } from "@/lib/gtm";

/** Map a legacy Signal source string to a CRM bridge form type. */
function toFormType(source?: string): WebsiteFormType {
  const s = (source || "").toLowerCase();
  if (s.includes("contact")) return "contact";
  if (s.includes("callback") || s.includes("call back")) return "callback";
  if (s.includes("hair analysis")) return "hair-analysis";
  if (s.includes("candidacy") || s.includes("suitab")) return "candidacy";
  if (s.includes("pdf")) return "pdf-download";
  if (s.includes("landing") || s.includes("google ads")) return "landing-page";
  if (s.includes("newsletter")) return "newsletter";
  return "consultation";
}

/**
 * Forwards a normalized lead to the Hair Art dashboard CRM bridge —
 * the single destination for all public website lead forms.
 */
async function forwardLeadToCrmBridge(lead: ClinicOsLead & { website?: string }): Promise<ClinicOsResult> {
  const result = await submitWebsiteLeadForm({
    formType: toFormType(lead.source),
    name: lead.name,
    phone: lead.phone || lead.whatsapp,
    email: lead.email,
    language: lead.language,
    website: lead.website,
    turnstile_token: lead.turnstile_token,
    message: lead.message || lead.notes,
    details: {
      treatment: lead.treatment || lead.treatment_interest || lead.procedure_interest,
      preferred_date: lead.preferred_date || lead.booking_date || lead.requested_date,
      preferred_time: lead.preferred_time,
      legacy_source: lead.source,
      source_page: lead.source_page,
    },
  });

  if (result.ok === true) return { ok: true, duplicate: result.duplicate, upstream_status: result.status };
  const known = new Set([
    "validation_error",
    "network_error",
    "server_misconfigured",
    "upstream_unreachable",
    "upstream_error",
    "function_not_found",
  ]);
  return {
    ok: false,
    error: known.has(result.error || "") ? (result.error as string) : "upstream_error",
    message: result.apiMessage,
  };


}



export type SubmitErrorCode =
  | "validation_error"
  | "network_error"
  | "server_misconfigured"
  | "upstream_unreachable"
  | "upstream_error"
  | "function_not_found"
  | "unknown";

export type SignalLeadEvent =
  | "form_started"
  | "validation_passed"
  | "turnstile_present"
  | "clinic_os_submit_started"
  | "clinic_os_submit_success"
  | "clinic_os_submit_failed"
  | "local_fallback_started"
  | "local_fallback_success"
  | "local_fallback_failed";

export type SubmitOutcome =
  | { status: "success"; duplicate?: boolean; result: ClinicOsResult; normalizedLead: ClinicOsLead }
  | { status: "unreachable"; error: string; errorCode: SubmitErrorCode; result?: ClinicOsResult; normalizedLead: ClinicOsLead };

export function logSignalLeadEvent(event: SignalLeadEvent, details: Record<string, unknown> = {}): void {
  // Keep this PII-safe: log stage/source/error only, never full lead payloads.

  console.info(`[signal-lead] ${event}`, details);
}

export function normalizePhoneNumber(phone?: string): string | undefined {
  const raw = (phone || "").trim();
  if (!raw) return undefined;

  let cleaned = raw.replace(/[\s().-]/g, "");
  cleaned = cleaned.replace(/(?!^)\+/g, "");
  if (cleaned.startsWith("00")) cleaned = `+${cleaned.slice(2)}`;

  if (cleaned.startsWith("+")) {
    return `+${cleaned.slice(1).replace(/\D/g, "")}`;
  }

  return cleaned.replace(/\D/g, "");
}

export function isValidLeadPhone(phone?: string): boolean {
  const normalized = normalizePhoneNumber(phone);
  if (!normalized) return false;
  const digits = normalized.replace(/\D/g, "");
  return digits.length >= 6 && digits.length <= 15;
}

function normalizeLead(lead: ClinicOsLead & { website?: string }): ClinicOsLead & { website?: string } {
  const phone = normalizePhoneNumber(lead.phone);
  const whatsapp = normalizePhoneNumber(lead.whatsapp);

  return {
    ...lead,
    name: lead.name?.trim(),
    email: lead.email?.trim().toLowerCase(),
    phone,
    whatsapp,
    message: lead.message?.trim(),
    notes: lead.notes?.trim(),
    treatment: lead.treatment?.trim(),
    treatment_interest: lead.treatment_interest?.trim(),
    procedure_interest: lead.procedure_interest?.trim(),
    source: lead.source?.trim() || "Signal Website",
  };
}

function toSubmitErrorCode(error?: string): SubmitErrorCode {
  switch (error) {
    case "validation_error":
    case "network_error":
    case "server_misconfigured":
    case "upstream_unreachable":
    case "upstream_error":
    case "function_not_found":
      return error;
    default:
      return "unknown";
  }
}

export async function submitSignalLead(lead: ClinicOsLead & { website?: string }): Promise<SubmitOutcome> {
  const normalizedLead = normalizeLead(lead);
  logSignalLeadEvent("clinic_os_submit_started", { source: normalizedLead.source });

  if (!normalizedLead.name && !normalizedLead.phone && !normalizedLead.email) {
    logSignalLeadEvent("clinic_os_submit_failed", { source: normalizedLead.source, errorCode: "validation_error" });
    return { status: "unreachable", error: "validation_error", errorCode: "validation_error", normalizedLead };
  }

  const result = await forwardLeadToCrmBridge(normalizedLead as ClinicOsLead & { website?: string });
  if (result.ok === true) {
    logSignalLeadEvent("clinic_os_submit_success", { source: normalizedLead.source, duplicate: !!result.duplicate });

    // Fire canonical Google Ads / GA4 conversion events for every successful lead
    // — form-level components can still fire more specific events on top.
    try {
      const isBooking = /booking|book|consultation/i.test(normalizedLead.source || "");
      trackLeadFormSubmit({
        form_name: normalizedLead.source,
        treatment_type: normalizedLead.treatment || normalizedLead.treatment_interest,
        source: normalizedLead.source,
        language: normalizedLead.language,
        duplicate: !!result.duplicate,
      });
      if (isBooking) {
        trackBookingCompleted({
          form_name: normalizedLead.source,
          treatment_type: normalizedLead.treatment || normalizedLead.treatment_interest,
          source: normalizedLead.source,
          language: normalizedLead.language,
          duplicate: !!result.duplicate,
        });
      }
    } catch (err) {
      console.warn("[signal-lead] analytics push failed", err);
    }


    return { status: "success", duplicate: !!result.duplicate, result, normalizedLead };
  }


  const failedResult = result as Extract<ClinicOsResult, { ok: false }>;
  const errorCode = toSubmitErrorCode(failedResult.error);
  logSignalLeadEvent("clinic_os_submit_failed", { source: normalizedLead.source, errorCode });
  return { status: "unreachable", error: failedResult.error, errorCode, result: failedResult, normalizedLead };
}

/**
 * Run a secondary action (email, local insert, analytics, WhatsApp automation)
 * without blocking the main lead flow. Errors are logged only.
 */
export async function runNonBlocking<T>(label: string, fn: () => Promise<T> | T): Promise<T | undefined> {
  try {
    return await fn();
  } catch (err) {

    console.warn(`[signal-lead] non-blocking step failed: ${label}`, err);
    return undefined;
  }
}

/**
 * Localized fallback message when Clinic OS is unreachable.
 * Directs the user to WhatsApp so no lead is lost.
 */
export function clinicOsUnreachableMessage(language: string): string {
  const map: Record<string, string> = {
    en: "We couldn't confirm your request right now. Please contact us on WhatsApp: +31 6 38523131.",
    nl: "We konden uw aanvraag nu niet bevestigen. Neem contact op via WhatsApp: +31 6 38523131.",
    fr: "Nous n'avons pas pu confirmer votre demande. Contactez-nous sur WhatsApp: +31 6 38523131.",
    tr: "Talebinizi şu anda doğrulayamadık. Lütfen WhatsApp ile iletişime geçin: +31 6 38523131.",
    ar: "تعذر تأكيد طلبك الآن. يرجى التواصل عبر واتساب: +31 6 38523131.",
  };
  return map[language] || map.en;
}

export function localFallbackReceivedMessage(language: string): string {
  const map: Record<string, string> = {
    en: "Your request has been received. If you need faster help, contact us on WhatsApp: +31 6 38523131.",
    nl: "Uw aanvraag is ontvangen. Voor snellere hulp kunt u ons bereiken via WhatsApp: +31 6 38523131.",
    fr: "Votre demande a été reçue. Pour une aide plus rapide, contactez-nous sur WhatsApp: +31 6 38523131.",
    tr: "Talebiniz alındı. Daha hızlı destek için WhatsApp üzerinden bize ulaşın: +31 6 38523131.",
    ar: "تم استلام طلبك. للحصول على مساعدة أسرع، تواصل معنا عبر واتساب: +31 6 38523131.",
  };
  return map[language] || map.en;
}

export { clinicOsDuplicateMessage };
